import type { OrganizationScopeKind } from '@/utils/organization'
import type { OptionFieldFilter } from '@/services/queries/utils/filters'
import {
  buildDisplayOptionFilter,
  buildPickerOptionFilter,
} from '@/services/queries/utils/filters'
import {
  isOptionDraftStatus,
  isOptionSelectableStatus,
  isOptionVisibleInConsumerStatus,
} from '@/services/property/option.status'

export type OptionListSourceKind = 'skop' | 'organization' | 'shared'

export type RawOptionListDefinition = {
  propertyKey: string
  key: string
  label: string
  propertyFieldId: string
  listId: string
  propertyOrganizationID?: string
}

export type ResolvedOptionListContext = RawOptionListDefinition & {
  ownerOrganizationID?: string
  ownerOrganizationName?: string
  activeOrganizationID?: string
  scopeKind?: OrganizationScopeKind
  sourceKind: OptionListSourceKind
  isShared: boolean
  isListOwner: boolean
  isListOwnerResolved: boolean
}

export type OptionAccessPolicy = {
  displayLookupFilter: OptionFieldFilter
  pickerFilter: OptionFieldFilter
  moderationFilter?: OptionFieldFilter
  canManageList: boolean
  canCreateInList: boolean
  canModerate: boolean
}

type BuildOptionAccessPolicyParams = {
  list: ResolvedOptionListContext
  userID?: string
  organizationFamilyIds?: readonly string[]
  isAdmin?: boolean
  isGuest?: boolean
  canCreate?: boolean
  canUpdate?: boolean
}

export const isOptionSelectable = (option: {
  id?: string
  status?: string | null
  selectableIds?: Set<string>
}) => {
  if (option.id && option.selectableIds) {
    return option.selectableIds.has(option.id)
  }

  return isOptionSelectableStatus(option.status)
}

export const isOptionVisibleInEditor = (option: { status?: string | null }) =>
  isOptionVisibleInConsumerStatus(option.status)

export const resolveOptionListContext = ({
  activeOrganizationID,
  ownerOrganizationID,
  ownerOrganizationName,
  propertyKey,
  propertyFieldId,
  propertyOrganizationID,
  key,
  label,
  listId,
  scopeKind,
}: RawOptionListDefinition & {
  activeOrganizationID?: string
  ownerOrganizationID?: string
  ownerOrganizationName?: string
  scopeKind?: OrganizationScopeKind
}): ResolvedOptionListContext => {
  const skopOrganizationID = process.env.NEXT_PUBLIC_SKOP_ORGID
  const resolvedOwnerOrganizationID =
    ownerOrganizationID ?? propertyOrganizationID
  const sourceKind: OptionListSourceKind =
    resolvedOwnerOrganizationID === skopOrganizationID
      ? 'skop'
      : resolvedOwnerOrganizationID &&
          propertyOrganizationID &&
          resolvedOwnerOrganizationID !== propertyOrganizationID
        ? 'shared'
        : propertyFieldId !== listId
          ? 'shared'
          : 'organization'
  const isShared = sourceKind === 'shared' || sourceKind === 'skop'
  const isListOwner = Boolean(
    resolvedOwnerOrganizationID &&
    activeOrganizationID &&
    resolvedOwnerOrganizationID === activeOrganizationID,
  )

  return {
    propertyKey,
    key,
    label,
    propertyFieldId,
    listId,
    propertyOrganizationID,
    ownerOrganizationID: resolvedOwnerOrganizationID,
    ownerOrganizationName,
    activeOrganizationID,
    scopeKind,
    sourceKind,
    isShared,
    isListOwner,
    isListOwnerResolved: Boolean(resolvedOwnerOrganizationID),
  }
}

export const buildOptionAccessPolicy = ({
  list,
  userID,
  organizationFamilyIds,
  isAdmin,
  isGuest,
  canCreate,
  canUpdate,
}: BuildOptionAccessPolicyParams): OptionAccessPolicy => {
  const displayLookupFilter = buildDisplayOptionFilter({
    activeOrganizationId: list.activeOrganizationID,
    organizationFamilyIds,
    propertyOrganizationID: list.ownerOrganizationID,
    isAdmin,
    scopeKind: list.scopeKind,
    userID,
  })
  const pickerFilter = buildPickerOptionFilter({
    activeOrganizationId: list.activeOrganizationID,
    organizationFamilyIds,
    propertyOrganizationID: list.ownerOrganizationID,
    isAdmin,
    scopeKind: list.scopeKind,
    userID,
  })

  const canManageList =
    !isGuest &&
    Boolean(canUpdate) &&
    list.isListOwnerResolved &&
    list.isListOwner
  const canCreateProposal =
    !isGuest &&
    Boolean(canCreate) &&
    list.scopeKind === 'organization' &&
    list.isListOwnerResolved &&
    !list.isListOwner &&
    list.isShared

  return {
    displayLookupFilter,
    pickerFilter,
    moderationFilter: canManageList ? undefined : pickerFilter,
    canManageList,
    canCreateInList: canManageList || canCreateProposal,
    canModerate: canManageList,
  }
}

export const canEditSharedOptionDraft = ({
  optionOrganizationID,
  optionUserID,
  optionStatus,
  organizationFamilyIDs,
  currentUserID,
  scopeKind,
}: {
  optionOrganizationID?: string
  optionUserID?: string
  optionStatus?: string
  organizationFamilyIDs?: readonly string[]
  currentUserID?: string
  scopeKind?: OrganizationScopeKind
}) => {
  if (!isOptionDraftStatus(optionStatus)) {
    return false
  }

  if (
    scopeKind === 'organization' &&
    optionOrganizationID &&
    organizationFamilyIDs?.includes(optionOrganizationID)
  ) {
    return true
  }

  return Boolean(
    optionUserID && currentUserID && optionUserID === currentUserID,
  )
}
